import { useMemo, useState } from 'react';
import { Empty, ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { api } from '../lib/api';
import {
  activityLabel,
  formatCompactCurrency,
  formatDate,
  formatNumber,
  humanise,
  todayLocal,
} from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  ArchivedDeal,
  Employee,
  PipelineStage,
  SalesAccount,
  SalesActivity,
  SalesOpportunity,
  SalesSummary,
} from '../lib/types';
import { OnboardingTab } from './sales/OnboardingTab';
import { AccountEditor, ActivityEditor, DealEditor } from './SalesEditors';

type Tab = 'pipeline' | 'accounts' | 'onboarding' | 'activity' | 'archived';

/**
 * Sales — the CRM for an MSP.
 *
 * Three views of the same data: the board (where deals are), accounts (who
 * they are with), and follow-ups (what has to happen next). The figures here
 * are the ones the Sales tile on the dashboard shows, read from the same
 * endpoint, because a tile that computes its own version of "pipeline"
 * eventually disagrees with the page it links to.
 */
export function SalesPage() {
  /* Archived deals count for the quarter they were archived in (the API's
     default, and what the dashboard card shows). A chosen period counts those
     archived inside it instead — null is "this quarter". */
  const [archivedRange, setArchivedRange] = useState<{ from: string; to: string } | null>(null);
  const summary = useResource<SalesSummary>(
    archivedRange
      ? `/sales/summary?${new URLSearchParams({ archivedFrom: archivedRange.from, archivedTo: archivedRange.to })}`
      : '/sales/summary',
  );
  const pipeline = useResource<PipelineStage[]>('/sales/pipeline');
  const accounts = useResource<SalesAccount[]>('/sales/accounts');
  const activities = useResource<SalesActivity[]>('/sales/activities');
  /* Archived deals — migration 170. Read with the rest rather than only when
     the tab is opened: it is one small query and the count belongs on the tab
     whether or not anybody clicks it. */
  const archived = useResource<ArchivedDeal[]>('/sales/opportunities/archived');
  const employees = useResource<Employee[]>('/employees?status=active');

  const [tab, setTab] = useState<Tab>('pipeline');
  const [owner, setOwner] = useState<string>('all');
  const [deal, setDeal] = useState<SalesOpportunity | 'new' | null>(null);
  const [account, setAccount] = useState<number | 'new' | null>(null);
  /* The account just created here. A new account is almost never the errand
     itself — a deal or an onboarding follows within seconds, and both ask for
     things the account form has just been told. Remembering it is what lets
     the next form open on that company with its answers already in place,
     rather than on whichever account happens to sort first. */
  const [justCreated, setJustCreated] = useState<SalesAccount | null>(null);
  const [activity, setActivity] = useState<SalesActivity | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reloadAll = () => {
    summary.reload();
    pipeline.reload();
    accounts.reload();
    activities.reload();
    archived.reload();
  };

  const handle = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save');

  const stages = pipeline.data ?? [];
  const allDeals = useMemo(() => stages.flatMap((s) => s.deals), [stages]);

  /* One entry per person who actually owns a deal — a full employee list would
     be mostly people who have never sold anything. */
  const owners = useMemo(() => {
    const seen = new Map<number, string>();
    for (const d of allDeals) {
      if (d.OwnerEmployeeId !== null) seen.set(d.OwnerEmployeeId, d.OwnerName ?? 'Unnamed');
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [allDeals]);

  const matchesOwner = (d: SalesOpportunity) =>
    owner === 'all' ||
    (owner === 'none' ? d.OwnerEmployeeId === null : d.OwnerEmployeeId === Number(owner));

  /* Only the FIRST load blanks the page. A background reload — after ticking
     an onboarding task, saving a deal, anything that calls reloadAll — would
     otherwise unmount everything below for a moment, and a tab with state of
     its own comes back reset: the onboarding workspace bounced the user out to
     the list on every save. */
  if (pipeline.loading && pipeline.data === null) return <Loading label="Loading sales" />;
  if (pipeline.error) return <ErrorBanner message={pipeline.error} />;

  const s = summary.data;
  const accountList = accounts.data ?? [];
  const nothingYet = accountList.length === 0 && allDeals.length === 0;

  return (
    <>
      <PageHead
        title="Sales"
        lede="Pipeline, accounts, onboarding and follow-ups — a deal from first contact to the day an account manager takes the client over. Recurring revenue and one-off work are tracked separately, because a $2k/month contract and a $2k hardware order are not the same deal."
        actions={
          <>
            <button type="button" className="btn btn-primary" onClick={() => setDeal('new')}>
              New deal
            </button>
          </>
        }
      />

      {error && <ErrorBanner message={error} />}

      {s && (
        <div className="row wrap mb-2" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
          <p className="xs muted grow">
            {s.ArchivedCounted === 0 ? 'No archived deals' : `${s.ArchivedCounted} archived ${s.ArchivedCounted === 1 ? 'deal' : 'deals'}`}{' '}
            counted — {archivedRange ? 'archived' : 'archived this quarter'}, {formatDate(s.ArchivedFrom)} to {formatDate(s.ArchivedTo)}
          </p>
          {archivedRange ? (
            <>
              <Field label="Archived from">
                <input
                  className="input"
                  type="date"
                  value={archivedRange.from}
                  max={archivedRange.to}
                  onChange={(e) => e.target.value && setArchivedRange({ ...archivedRange, from: e.target.value })}
                />
              </Field>
              <Field label="to">
                <input
                  className="input"
                  type="date"
                  value={archivedRange.to}
                  min={archivedRange.from}
                  onChange={(e) => e.target.value && setArchivedRange({ ...archivedRange, to: e.target.value })}
                />
              </Field>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setArchivedRange(null)}>
                This quarter
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setArchivedRange({ from: String(s.ArchivedFrom).slice(0, 10), to: String(s.ArchivedTo).slice(0, 10) })}
            >
              Include archived deals from…
            </button>
          )}
        </div>
      )}

      {s && (
        <div className="grid grid-4 mb-4">
          <div className="stat">
            <div className="label">Open pipeline</div>
            <div className="figure">{formatCompactCurrency(s.OpenPipelineValue)}</div>
            <div className="foot">
              {s.OpenDeals} open {s.OpenDeals === 1 ? 'deal' : 'deals'} ·{' '}
              {formatNumber(s.OpenPipelineMrr, 'currency')}/mo
            </div>
          </div>
          <div className="stat">
            <div className="label">Weighted forecast</div>
            <div className="figure">{formatCompactCurrency(s.WeightedForecast)}</div>
            <div className="foot">at stage probability</div>
          </div>
          <div className="stat">
            <div className="label">Won this quarter</div>
            <div className="figure">{formatCompactCurrency(s.WonMrrThisQuarter)}</div>
            <div className="foot">
              MRR from {s.WonThisQuarter} {s.WonThisQuarter === 1 ? 'deal' : 'deals'}
              {s.LostThisQuarter > 0 ? ` · ${s.LostThisQuarter} lost` : ''}
            </div>
          </div>
          <div className="stat">
            <div className="label">Win rate</div>
            <div className="figure">
              {s.WinRate === null ? '—' : `${Math.round(s.WinRate)}%`}
            </div>
            <div className="foot">
              {s.WinRate === null ? 'nothing closed yet' : 'trailing twelve months'}
            </div>
          </div>
        </div>
      )}

      {/* The three ways a pipeline rots. Each is a list someone can work
          through this afternoon, so they are counted separately rather than
          rolled into one "health" number nobody can act on. */}
      {s && (s.OverdueCloseDates > 0 || s.DealsWithoutNextStep > 0 || s.OverdueActivities > 0) && (
        <div className="card mb-4">
          <div className="card-body tight">
            <p className="small">
              <span className="eyebrow">Needs working</span>
            </p>
            <p className="small muted mt-2">
              {[
                s.OverdueCloseDates > 0 &&
                  (s.OverdueCloseDates === 1
                    ? '1 deal is past its expected close date'
                    : `${s.OverdueCloseDates} deals are past their expected close date`),
                s.DealsWithoutNextStep > 0 &&
                  `${s.DealsWithoutNextStep} ${s.DealsWithoutNextStep === 1 ? 'deal has' : 'deals have'} no next step`,
                s.OverdueActivities > 0 &&
                  `${s.OverdueActivities} ${s.OverdueActivities === 1 ? 'follow-up is' : 'follow-ups are'} overdue`,
              ]
                .filter(Boolean)
                .join(' · ')}
              .
            </p>
          </div>
        </div>
      )}

      {/* The empty state stands down for the onboarding tab: the wizard there
          creates its own account, so a signed client with no pipeline behind
          them must still have a way in. */}
      {nothingYet && tab !== 'onboarding' ? (
        <Empty title="No accounts yet">
          <p>
            Start with a company — prospect or existing client — then put a deal against it. Clients
            belong here too: most MSP growth is the second contract, not the first logo.
          </p>
          <div className="row wrap mt-4">
            <button type="button" className="btn btn-primary" onClick={() => setAccount('new')}>
              Add the first account
            </button>
            {/* The other way in. An order that arrives already signed has no
                pipeline behind it, and the wizard creates the account itself —
                so an empty board must not be a dead end for it. */}
            <button type="button" className="btn btn-ghost" onClick={() => setTab('onboarding')}>
              Onboard a signed client
            </button>
          </div>
        </Empty>
      ) : (
        <>
          <div className="tabs">
            <button
              type="button"
              className={`tab${tab === 'pipeline' ? ' active' : ''}`}
              onClick={() => setTab('pipeline')}
            >
              Pipeline
            </button>
            <button
              type="button"
              className={`tab${tab === 'accounts' ? ' active' : ''}`}
              onClick={() => setTab('accounts')}
            >
              Accounts ({accountList.length})
            </button>
            {/* Between accounts and follow-ups, because that is the order the
                work happens in: a deal is won against an account, the client is
                onboarded, and then somebody else owns them.

                Named for the far end rather than the middle. What Sales needs
                from this tab is the point at which a client stops being theirs
                — the tasks themselves are run from Client Onboarding, under
                Operate. The tab id stays `onboarding`: it is what the tables,
                the endpoints and the deep links are called, and renaming a
                label is not a reason to rename all of that. */}
            <button
              type="button"
              className={`tab${tab === 'onboarding' ? ' active' : ''}`}
              onClick={() => setTab('onboarding')}
            >
              Handover to AM
            </button>
            <button
              type="button"
              className={`tab${tab === 'activity' ? ' active' : ''}`}
              onClick={() => setTab('activity')}
            >
              Follow-ups
            </button>
            {/* Last, and only once there is something in it: an empty tab
                teaches nobody anything, and a deal is archived from the deal
                itself rather than from here. */}
            {(archived.data?.length ?? 0) > 0 && (
              <button
                type="button"
                className={`tab${tab === 'archived' ? ' active' : ''}`}
                onClick={() => setTab('archived')}
              >
                Archived ({archived.data?.length ?? 0})
              </button>
            )}
          </div>

          {tab === 'pipeline' && (
            <>
              <div className="row-between wrap mb-4">
                {owners.length > 0 ? (
                  <div className="row">
                    <span className="xs muted">Owner</span>
                    <select
                      className="select"
                      style={{ width: 'auto' }}
                      value={owner}
                      onChange={(e) => setOwner(e.target.value)}
                    >
                      <option value="all">Everyone</option>
                      {owners.map(([id, name]) => (
                        <option key={id} value={String(id)}>
                          {name}
                        </option>
                      ))}
                      <option value="none">Unassigned</option>
                    </select>
                  </div>
                ) : (
                  <span />
                )}
                <BoardMrr deals={allDeals.filter(matchesOwner)} />
              </div>

              <Board
                stages={stages}
                filter={matchesOwner}
                onOpen={setDeal}
                onMove={(opportunityId, stageId) =>
                  void api
                    .put(`/sales/opportunities/${opportunityId}`, { stageId })
                    .then(reloadAll)
                    .catch(handle)
                }
              />
            </>
          )}

          {tab === 'accounts' && (
            <AccountsTable accounts={accountList} onOpen={(id) => setAccount(id)} />
          )}

          {tab === 'onboarding' && (
            <OnboardingTab
              accounts={accountList}
              deals={allDeals}
              employees={employees.data ?? []}
              defaultAccountId={justCreated?.AccountId ?? null}
              onAccountsChanged={reloadAll}
            />
          )}

          {tab === 'archived' && (
            <ArchivedDeals
              deals={archived.data ?? []}
              loading={archived.loading}
              onRestore={(d) =>
                void api
                  .put(`/sales/opportunities/${d.OpportunityId}/archive`, { archived: false })
                  .then(reloadAll)
                  .catch(handle)
              }
            />
          )}

          {tab === 'activity' && (
            <ActivityList
              activities={activities.data ?? []}
              loading={activities.loading}
              onOpen={setActivity}
              onAdd={() => setActivity('new')}
              onComplete={(a) =>
                void api
                  .put(`/sales/activities/${a.ActivityId}`, { completedOn: todayLocal() })
                  .then(reloadAll)
                  .catch(handle)
              }
            />
          )}
        </>
      )}

      {deal && (
        <DealEditor
          deal={deal === 'new' ? null : deal}
          accounts={accountList}
          stages={stages}
          employees={employees.data ?? []}
          defaultAccountId={justCreated?.AccountId ?? null}
          onClose={() => setDeal(null)}
          onSaved={() => {
            setDeal(null);
            reloadAll();
          }}
        />
      )}

      {account !== null && (
        <AccountEditor
          accountId={account === 'new' ? null : account}
          employees={employees.data ?? []}
          onClose={() => setAccount(null)}
          onSaved={(created) => {
            setAccount(null);
            if (created) setJustCreated(created);
            reloadAll();
          }}
          onOpenDeal={(d) => {
            setAccount(null);
            setDeal(d);
          }}
        />
      )}

      {activity && (
        <ActivityEditor
          activity={activity === 'new' ? null : activity}
          accounts={accountList}
          deals={allDeals}
          employees={employees.data ?? []}
          onClose={() => setActivity(null)}
          onSaved={() => {
            setActivity(null);
            reloadAll();
          }}
        />
      )}
    </>
  );
}

/* =========================================================================
   The board
   ========================================================================= */

/**
 * Monthly recurring revenue across every deal on the board — every column,
 * won and lost included, because that is what the board shows. Follows the
 * Owner filter for the same reason. Each column's own MRR is under its header,
 * so the total can be checked against them.
 */
function BoardMrr({ deals }: { deals: SalesOpportunity[] }) {
  const total = deals.reduce((sum, d) => sum + d.Mrr, 0);
  const withMrr = deals.filter((d) => d.Mrr > 0).length;
  return (
    <div className="board-mrr" title={`${withMrr} of ${deals.length} deals on the board carry MRR`}>
      <span className="xs muted">Monthly MRR, all deals on the board</span>
      <span className="mono strong">{formatNumber(total, 'currency')}/mo</span>
    </div>
  );
}

function Board({
  stages,
  filter,
  onOpen,
  onMove,
}: {
  stages: PipelineStage[];
  filter: (deal: SalesOpportunity) => boolean;
  onOpen: (deal: SalesOpportunity) => void;
  onMove: (opportunityId: number, stageId: number) => void;
}) {
  const [over, setOver] = useState<number | null>(null);

  return (
    <div className="pipeline">
      {stages.map((stage) => {
        const deals = stage.deals.filter(filter);
        const value = deals.reduce((sum, d) => sum + d.AnnualValue, 0);
        const mrr = deals.reduce((sum, d) => sum + d.Mrr, 0);

        return (
          <section
            key={stage.StageId}
            className={`pipe-col${over === stage.StageId ? ' over' : ''}${stage.IsClosed ? ' closed' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(stage.StageId);
            }}
            onDragLeave={() => setOver((c) => (c === stage.StageId ? null : c))}
            onDrop={(e) => {
              e.preventDefault();
              setOver(null);
              const id = Number(e.dataTransfer.getData('text/plain'));
              if (id) onMove(id, stage.StageId);
            }}
          >
            <header className="pipe-head">
              <div className="row-between">
                <h3>{stage.Name}</h3>
                <span className="xs muted mono">{deals.length}</span>
              </div>
              <div className="row-between">
                <span className="mono small">{formatCompactCurrency(value)}</span>
                {!stage.IsClosed && <span className="xs subtle">{Math.round(stage.Probability)}%</span>}
              </div>
              {mrr > 0 && <span className="xs muted mono">{formatCompactCurrency(mrr)}/mo MRR</span>}
            </header>

            <div className="pipe-body">
              {deals.length === 0 && <p className="xs subtle">Nothing here</p>}
              {deals.map((d) => (
                <DealCard key={d.OpportunityId} deal={d} onOpen={() => onOpen(d)} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function DealCard({ deal, onOpen }: { deal: SalesOpportunity; onOpen: () => void }) {
  /* Dragging moves a deal between stages. The card is a button as well, so the
     same move is available from the keyboard through the stage select in the
     editor — drag-and-drop alone would lock keyboard users out of the one
     interaction this page is built around. */
  return (
    <button
      type="button"
      className={`deal${deal.IsOverdue ? ' flag' : ''}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/plain', String(deal.OpportunityId))}
      onClick={onOpen}
    >
      <span className="deal-name">{deal.Name}</span>
      <span className="deal-account">{deal.AccountName}</span>

      <span className="deal-figures">
        <span className="mono">{formatCompactCurrency(deal.AnnualValue)}</span>
        {deal.Mrr > 0 && (
          <span className="xs muted mono">{formatCompactCurrency(deal.Mrr)}/mo</span>
        )}
      </span>

      <span className="deal-foot">
        <span className="xs muted">{deal.OwnerName ?? 'Unassigned'}</span>
        {!deal.IsClosed && (
          <span className={`xs${deal.IsOverdue ? ' accent' : ' muted'} mono`}>
            {formatDate(deal.ExpectedCloseDate)}
          </span>
        )}
        {deal.IsClosed && <span className="xs muted mono">{formatDate(deal.ClosedOn)}</span>}
      </span>

      {!deal.IsClosed &&
        (deal.NextStep ? (
          <span className={`deal-next xs${deal.NextStepOverdue ? ' overdue' : ''}`}>
            {deal.NextStep}
          </span>
        ) : (
          <span className="deal-next xs missing">No next step</span>
        ))}
    </button>
  );
}

/**
 * The deals that were taken off the board — migration 170.
 *
 * Everything, newest archived first, with no age cutoff: this IS the looking
 * back, so trimming it the way the board trims closed deals would defeat it.
 * Restore puts a deal back exactly as it was — nothing was changed when it
 * went, so nothing has to be rebuilt to bring it home.
 *
 * These deals are still in the scorecard above. Archiving is about the board,
 * not the record.
 */
function ArchivedDeals({
  deals,
  loading,
  onRestore,
}: {
  deals: ArchivedDeal[];
  loading: boolean;
  onRestore: (deal: ArchivedDeal) => void;
}) {
  if (loading) return <Loading label="Reading the archived deals" />;
  if (deals.length === 0) {
    return (
      <Empty title="Nothing archived">
        <p>Archive a deal from the deal itself to take it off the board without deleting it.</p>
      </Empty>
    );
  }

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <h3>
            {deals.length} archived deal{deals.length === 1 ? '' : 's'}
          </h3>
          <p className="sub">
            Off the board, still in the quarter's figures, and still here. Most recently archived
            first.
          </p>
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Deal</th>
              <th>Account</th>
              <th>Stage</th>
              <th className="num">Value</th>
              <th>Owner</th>
              <th style={{ width: 150 }}>Archived</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {deals.map((d) => (
              <tr key={d.OpportunityId}>
                <td className="strong">{d.Name}</td>
                <td>{d.AccountName ?? '—'}</td>
                <td className="xs">{d.StageName}</td>
                <td className="num mono">
                  {formatCompactCurrency(d.AnnualValue)}
                  {d.Mrr > 0 && (
                    <div className="xs muted">{formatCompactCurrency(d.Mrr)}/mo</div>
                  )}
                </td>
                <td className="xs">{d.OwnerName ?? 'Unassigned'}</td>
                <td className="xs">
                  {formatDate(d.ArchivedUtc)}
                  {d.ArchivedBy && <div className="subtle">{d.ArchivedBy}</div>}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title="Put it back on the board, exactly as it was"
                    onClick={() => onRestore(d)}
                  >
                    Restore
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* =========================================================================
   Accounts
   ========================================================================= */

function AccountsTable({
  accounts,
  onOpen,
}: {
  accounts: SalesAccount[];
  onOpen: (accountId: number) => void;
}) {
  if (accounts.length === 0) {
    return (
      <Empty title="No accounts">
        <p>Add a company to sell to.</p>
      </Empty>
    );
  }

  return (
    <div className="card">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Company</th>
              <th style={{ width: 110 }}>Relationship</th>
              <th style={{ width: 170 }}>Primary contact</th>
              <th style={{ width: 160 }}>Account manager</th>
              <th className="num" style={{ width: 70 }}>Seats</th>
              <th className="num" style={{ width: 110 }}>Current MRR</th>
              <th className="num" style={{ width: 130 }}>Open pipeline</th>
              <th style={{ width: 110 }}>Last touch</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.AccountId}>
                <td>
                  <div className="strong">{a.Name}</div>
                  {(a.City || a.Industry) && (
                    <div className="xs muted">
                      {[a.Industry, [a.City, a.Region].filter(Boolean).join(', ')]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}
                </td>
                <td>
                  <span
                    className={`badge ${a.AccountType === 'client' ? 'badge-good' : 'badge-neutral'}`}
                  >
                    {humanise(a.AccountType)}
                  </span>
                </td>
                <td>{a.PrimaryContactName ?? <span className="subtle">Nobody named</span>}</td>
                <td>{a.OwnerName ?? <span className="subtle">—</span>}</td>
                <td className="num">{a.Seats ?? '—'}</td>
                <td className="num">
                  {a.CurrentMrr ? formatNumber(a.CurrentMrr, 'currency') : '—'}
                </td>
                <td className="num">
                  {a.OpenDeals && a.OpenDeals > 0 ? (
                    <>
                      {formatCompactCurrency(a.PipelineValue ?? 0)}
                      <span className="xs muted"> · {a.OpenDeals}</span>
                    </>
                  ) : (
                    <span className="subtle">—</span>
                  )}
                </td>
                <td className="mono xs">{formatDate(a.LastActivityOn)}</td>
                <td className="actions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => onOpen(a.AccountId)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =========================================================================
   Follow-ups
   ========================================================================= */

function ActivityList({
  activities,
  loading,
  onOpen,
  onAdd,
  onComplete,
}: {
  activities: SalesActivity[];
  loading: boolean;
  onOpen: (activity: SalesActivity) => void;
  onAdd: () => void;
  onComplete: (activity: SalesActivity) => void;
}) {
  if (loading) return <Loading label="Loading activity" />;

  const open = activities.filter((a) => a.CompletedOn === null);
  const done = activities.filter((a) => a.CompletedOn !== null);

  return (
    <div className="stack">
      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Outstanding</span>
            <h2>Follow-ups</h2>
            <p className="sub">Calls, meetings and proposals that have not happened yet.</p>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={onAdd}>
            Log activity
          </button>
        </div>

        {open.length === 0 ? (
          <div className="card-body">
            <p className="muted small">Nothing outstanding.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Due</th>
                  <th style={{ width: 100 }}>Type</th>
                  <th>What</th>
                  <th style={{ width: 180 }}>Account</th>
                  <th style={{ width: 150 }}>Owner</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {open.map((a) => (
                  <tr key={a.ActivityId}>
                    <td className="mono xs">
                      {a.IsOverdue ? (
                        <span className="badge badge-critical">{formatDate(a.DueDate)}</span>
                      ) : (
                        formatDate(a.DueDate)
                      )}
                    </td>
                    <td className="xs">{activityLabel(a.ActivityType)}</td>
                    <td>
                      <div className="strong">{a.Subject}</div>
                      {a.OpportunityName && <div className="xs muted">{a.OpportunityName}</div>}
                    </td>
                    <td>{a.AccountName}</td>
                    <td>{a.OwnerName ?? <span className="subtle">—</span>}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onComplete(a)}
                      >
                        Done
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onOpen(a)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {done.length > 0 && (
        <section className="card">
          <div className="card-head">
            <div>
              <span className="eyebrow">History</span>
              <h2>Logged</h2>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 100 }}>Type</th>
                  <th>What</th>
                  <th style={{ width: 180 }}>Account</th>
                  <th style={{ width: 150 }}>Owner</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {done.map((a) => (
                  <tr key={a.ActivityId}>
                    <td className="mono xs">{formatDate(a.CompletedOn)}</td>
                    <td className="xs">{activityLabel(a.ActivityType)}</td>
                    <td>
                      <div className="strong">{a.Subject}</div>
                      {a.OpportunityName && <div className="xs muted">{a.OpportunityName}</div>}
                    </td>
                    <td>{a.AccountName}</td>
                    <td>{a.OwnerName ?? <span className="subtle">—</span>}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onOpen(a)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
